import type {
  NotificationCatalog,
  NotificationChannel,
  NotificationDefinition,
  NotificationDefinitionInput,
  NotificationDefinitionUpdate,
  NotificationSummary,
  NotificationTemplate,
  NotificationTemplateUpdate
} from "@kuroo/contracts";

export type SdkNotificationsContext = {
  request: <T>(path: string, init?: RequestInit) => Promise<T>;
};

export function notifications(context: SdkNotificationsContext, options: { status?: string; channel?: NotificationChannel; definitionId?: string; recipient?: string } = {}): Promise<NotificationSummary[]> {
  const query = new URLSearchParams();
  if (options.status) query.set("status", options.status);
  if (options.channel) query.set("channel", options.channel);
  if (options.definitionId) query.set("definitionId", options.definitionId);
  if (options.recipient) query.set("recipient", options.recipient);
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return context.request<NotificationSummary[]>(`/api/v1/notifications${suffix}`);
}

export function notificationCatalog(context: SdkNotificationsContext): Promise<NotificationCatalog> {
  return context.request<NotificationCatalog>("/api/v1/notification-definitions");
}

export function createNotificationDefinition(context: SdkNotificationsContext, input: NotificationDefinitionInput): Promise<NotificationDefinition> {
  return context.request<NotificationDefinition>("/api/v1/notification-definitions", { method: "POST", body: JSON.stringify(input) });
}

export function updateNotificationDefinition(context: SdkNotificationsContext, definitionId: string, input: NotificationDefinitionUpdate): Promise<NotificationDefinition> {
  return context.request<NotificationDefinition>(`/api/v1/notification-definitions/${encodeURIComponent(definitionId)}`, { method: "PATCH", body: JSON.stringify(input) });
}

export function deleteNotificationDefinition(context: SdkNotificationsContext, definitionId: string): Promise<{ id: string; deleted: boolean }> {
  return context.request<{ id: string; deleted: boolean }>(`/api/v1/notification-definitions/${encodeURIComponent(definitionId)}`, { method: "DELETE", body: "{}" });
}

export function updateNotificationDefinitionTemplate(context: SdkNotificationsContext, definitionId: string, channel: NotificationChannel, input: NotificationTemplateUpdate): Promise<NotificationTemplate> {
  return context.request<NotificationTemplate>(`/api/v1/notification-definitions/${encodeURIComponent(definitionId)}/templates/${encodeURIComponent(channel)}`, { method: "PATCH", body: JSON.stringify(input) });
}

export function sendNotificationTest(context: SdkNotificationsContext, definitionId: string, input: { channel: Exclude<NotificationChannel, "push">; recipient: string; variables?: Record<string, unknown> }): Promise<NotificationSummary> {
  return context.request<NotificationSummary>(`/api/v1/notification-definitions/${encodeURIComponent(definitionId)}/test`, { method: "POST", body: JSON.stringify(input) });
}

export function notificationTemplates(context: SdkNotificationsContext): Promise<NotificationTemplate[]> {
  return context.request<NotificationTemplate[]>("/api/v1/notification-templates");
}

export function createNotificationTemplate(context: SdkNotificationsContext, input: { channel: NotificationChannel; eventType: string; locale?: string; subject?: string; body: string; enabled?: boolean; format?: "html" | "text"; whatsappTemplate?: Record<string, unknown> | null }): Promise<NotificationTemplate> {
  return context.request<NotificationTemplate>("/api/v1/notification-templates", { method: "POST", body: JSON.stringify(input) });
}

export function updateNotificationTemplate(context: SdkNotificationsContext, templateId: string, input: { channel?: NotificationChannel; subject?: string | null; body?: string; enabled?: boolean; format?: "html" | "text"; whatsappTemplate?: Record<string, unknown> | null }): Promise<{ id: string; updated: boolean }> {
  return context.request<{ id: string; updated: boolean }>(`/api/v1/notification-templates/${encodeURIComponent(templateId)}`, { method: "PATCH", body: JSON.stringify(input) });
}

export function sendNotification(context: SdkNotificationsContext, input: { channel?: NotificationChannel; templateId?: string; bookingId?: string; recipient: string; scheduledAt?: string; variables?: Record<string, unknown>; idempotencyKey?: string }): Promise<{ id: string; channel: NotificationChannel; recipient: string; status: "queued"; scheduledAt: string }> {
  const idempotencyKey = input.idempotencyKey ?? `notification-${Date.now()}-${globalThis.crypto?.randomUUID?.() ?? `${Date.now()}`}`;
  return context.request<{ id: string; channel: NotificationChannel; recipient: string; status: "queued"; scheduledAt: string }>("/api/v1/notifications", { method: "POST", body: JSON.stringify({ ...input, idempotencyKey }), headers: { "Idempotency-Key": idempotencyKey } });
}

export function retryNotification(context: SdkNotificationsContext, deliveryId: string): Promise<{ id: string; status: "queued" }> {
  return context.request<{ id: string; status: "queued" }>(`/api/v1/notifications/${encodeURIComponent(deliveryId)}/retry`, { method: "POST", body: "{}" });
}
